/**
 * SpendingLimitAlert - Component to display proactive spending limit warnings
 */
import { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { Link } from 'react-router-dom';
import { getSpendingStatus, sendSpendingAlert } from '../lib/api';
import { 
  AlertTriangle, 
  CreditCard, 
  Bell, 
  ChevronRight, 
  X,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const SpendingLimitAlert = () => {
  const [spendingStatus, setSpendingStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [sendingAlert, setSendingAlert] = useState(false);

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await getSpendingStatus();
        setSpendingStatus(response.data);
      } catch (error) {
        // Silently fail - this is a non-critical feature
      } finally {
        setLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleSendAlert = async (cardId) => {
    setSendingAlert(true);
    try {
      const response = await sendSpendingAlert(cardId);
      if (response.data.sent) {
        toast.success('Spending alert sent to your email!');
      } else {
        toast.info(response.data.message || 'All cards within limits');
      }
    } catch (error) {
      toast.error('Failed to send alert');
    } finally {
      setSendingAlert(false);
    }
  };

  if (loading || dismissed || !spendingStatus?.cards_at_risk) {
    return null;
  }

  const cardsAtRisk = spendingStatus.cards.filter(c => c.needs_attention);

  if (cardsAtRisk.length === 0) {
    return null;
  }

  const hasCritical = cardsAtRisk.some(c => c.status === 'critical');

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        data-testid="spending-limit-alert"
      >
        <Card className={`border-2 ${hasCritical ? 'border-red-500/50 bg-red-500/5' : 'border-amber-500/50 bg-amber-500/5'}`}>
          <CardContent className="p-4">
            <div className="flex items-start gap-4">
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                hasCritical ? 'bg-red-500/20' : 'bg-amber-500/20'
              }`}>
                <AlertTriangle className={`w-5 h-5 ${hasCritical ? 'text-red-500' : 'text-amber-500'}`} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h3 className={`font-semibold ${hasCritical ? 'text-red-600' : 'text-amber-600'}`}>
                    {hasCritical ? 'Critical: Spending Limit Alert' : 'Approaching Spending Limits'}
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6" 
                    onClick={() => setDismissed(true)}
                    data-testid="dismiss-alert-btn"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-3">
                  {cardsAtRisk.map((card) => (
                    <div 
                      key={card.card_id} 
                      className="flex items-center gap-3 p-3 rounded-lg bg-background/80"
                      data-testid={`alert-card-${card.card_last_four}`}
                    >
                      <CreditCard className={`w-5 h-5 flex-shrink-0 ${
                        card.status === 'critical' ? 'text-red-500' : 'text-amber-500'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-mono text-sm">•••• {card.card_last_four}</span>
                          <span className={`text-sm font-semibold ${
                            card.status === 'critical' ? 'text-red-600' : 'text-amber-600'
                          }`}>
                            {card.percent_used.toFixed(0)}%
                          </span>
                        </div>
                        <Progress 
                          value={Math.min(card.percent_used, 100)} 
                          className={`h-1.5 mt-1 ${
                            card.status === 'critical' ? '[&>div]:bg-red-500' : '[&>div]:bg-amber-500'
                          }`}
                        />
                        <div className="flex justify-between mt-1">
                          <span className="text-xs text-muted-foreground">
                            ${card.spent.toFixed(2)} / ${card.limit.toFixed(2)}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            ${card.remaining.toFixed(2)} left
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex items-center gap-3 mt-4 flex-wrap">
                  <Link to="/cards">
                    <Button size="sm" variant="outline" className="rounded-full" data-testid="view-cards-btn">
                      <CreditCard className="w-4 h-4 mr-2" />
                      View Cards
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="rounded-full"
                    onClick={() => handleSendAlert()}
                    disabled={sendingAlert}
                    data-testid="send-alert-btn"
                  >
                    {sendingAlert ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Bell className="w-4 h-4 mr-2" />
                    )}
                    Send Alert to Email
                  </Button>
                </div>
                
                <p className="text-xs text-muted-foreground mt-3">
                  {hasCritical 
                    ? 'Transactions may be declined if you exceed your spending limit.' 
                    : 'Consider reviewing your spending to avoid hitting your limit.'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </AnimatePresence>
  );
};

export default SpendingLimitAlert;
